import {
  selectMergedSelectedAddons,
  selectSelectedAddons,
  selectSelectedBundles,
  selectTotalPriceOfTopUps,
  selectWalletCredit,
} from 'app/pages/TicketSalePage/slice/selectors';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  selectCartEventCurrency,
  selectEventFromCart,
  selectSelectedTicketsFromCart,
  selectVoucherDataFromCart,
} from 'store/cartSlice/selectors';
import { selectMerchantTaxName } from 'store/vendorSlice/selectors';
import { formatMoney } from 'utils/formatMoney';
import { getCartTotal } from 'utils/getItemsTotal';
import { buildPriceToShow, getTotalOfItem } from '../../../utils/tickets';
import CartItem from '../CartTotal/CartItem';
import CartTicket, { getCartTicketKey } from '../CartTotal/CartTicket';
import CartBundle, { getCartBundleKey } from '../CartTotal/CartBundle';

const AddonsPageTotal: React.FC<{}> = memo(() => {
  const { t } = useTranslation();

  const selectedTickets = useSelector(selectSelectedTicketsFromCart);
  const selectedBundles = useSelector(selectSelectedBundles);
  const selectedMergedAddons = useSelector(selectMergedSelectedAddons);
  const selectedAddons = useSelector(selectSelectedAddons);
  const totalPriceOfTopUps = useSelector(selectTotalPriceOfTopUps);
  const walletData = useSelector(selectWalletCredit);
  const eventCurrency = useSelector(selectCartEventCurrency);
  const selectedEvent = useSelector(selectEventFromCart);
  const merchantTaxName = useSelector(selectMerchantTaxName);
  const voucher = useSelector(selectVoucherDataFromCart);

  let { cartTotal, taxTotal, feeTotal } = getCartTotal(
    [...selectedTickets, ...selectedBundles, ...selectedAddons],
    selectedEvent,
    voucher,
  );

  if (totalPriceOfTopUps.sum > 0) {
    cartTotal += totalPriceOfTopUps.sum;
    taxTotal += totalPriceOfTopUps.tax;
  }

  return (
    <div className="rounded-md mt-3 sm:mt-4 lg:mt-0 bg-neutral-10 p-[16px] text-neutral-1000 h-full lg:w-1/3 max-h-[540px]">
      <p className="p1-medium mb-[4px]">{t('cartSummary.cartSummaryTitle')}</p>
      {selectedBundles.map(bundle => (
        <CartBundle
          key={getCartBundleKey(bundle)}
          bundle={bundle}
          event={selectedEvent}
        />
      ))}
      {selectedTickets.map(ticket => (
        <CartTicket
          key={getCartTicketKey(ticket)}
          ticket={ticket}
          event={selectedEvent}
          voucher={voucher.voucherData}
          voucherApplicableTickets={voucher.voucherApplicableTickets}
        />
      ))}
      {selectedMergedAddons.map(item => (
        <CartItem
          name={`${item.quantity} x ${item.name}`}
          price={buildPriceToShow(
            getTotalOfItem(item.price, item.quantity),
            eventCurrency,
            formatMoney(0, eventCurrency),
          )}
          size={2}
        />
      ))}
      {walletData && totalPriceOfTopUps.sum > 0 ? (
        <CartItem
          name={t('cartSummary.topUps')}
          price={buildPriceToShow(
            totalPriceOfTopUps.sum,
            walletData.currency,
            formatMoney(0, walletData.currency),
          )}
          size={2}
        />
      ) : null}
      {taxTotal > 0 ? (
        <CartItem
          name={merchantTaxName}
          price={buildPriceToShow(taxTotal, eventCurrency)}
          size={4}
        />
      ) : null}
      {feeTotal > 0 ? (
        <CartItem
          name={t('ticketSale.fee')}
          price={buildPriceToShow(feeTotal, eventCurrency)}
          size={4}
        />
      ) : null}
      <div className="flex justify-between pt-[8px]">
        <p className="p1-semibold uppercase">{t('ticketSale.total')}</p>
        <p className="p1-semibold">
          {buildPriceToShow(
            cartTotal + taxTotal + feeTotal,
            eventCurrency,
            formatMoney(0, eventCurrency),
          )}
        </p>
      </div>
    </div>
  );
});

export default AddonsPageTotal;
